import cn from "classnames";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import Chip from "components/Chip";
import Menu from "components/Menu";
import memoize from "lodash/memoize";
import React, { Component } from "react";
import { LEASE_TERMS } from "util/constants";
import RadioGroup from "components/RadioGroup";
import ExternalClickListener from "components/ExternalClickListener";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";

import styles from "./styles.module.scss";

const FLEXIBLE_LEASE_TERM = "FLEXIBLE";

const determineIfLeaseTermIsFlexible = memoize(
  (leaseTerm) => leaseTerm === FLEXIBLE_LEASE_TERM
);

export default class LeaseTermFilter extends Component {
  constructor(props, context) {
    super(props);
    this.state = {
      leaseTerm: FLEXIBLE_LEASE_TERM,
      leaseTermMax: context.values.leaseTermMax,
      leaseTermMin: context.values.leaseTermMin,
    };
  }

  componentDidMount() {
    this.mapLeaseTermToState();
  }

  componentDidUpdate() {
    const { open } = this.props;
    const { values } = this.context;
    const { leaseTermMax, leaseTermMin } = this.state;
    // update state when drawer is closed and on new context values
    // props.open needs to be false to prevent exceeding call stack when changing value.
    if (
      !open &&
      (values.leaseTermMax !== leaseTermMax ||
        values.leaseTermMin !== leaseTermMin)
    ) {
      this.mapLeaseTermToState();
    }
  }

  mapLeaseTermToState = () => {
    const {
      values: { leaseTermMin, leaseTermMax },
      defaultValues,
    } = this.context;

    this.LEASE_TERM_VALUES = {
      LESS_THAN_ONE_YEAR: {
        leaseTermMin: defaultValues.leaseTermMin,
        leaseTermMax: 12,
      },
      ONE_TO_TWO_YEARS: { leaseTermMin: 12, leaseTermMax: 36 },
      THREE_TO_FIVE_YEARS: { leaseTermMin: 36, leaseTermMax: 60 },
      MORE_THAN_FIVE_YEARS: {
        leaseTermMin: 60,
        leaseTermMax: defaultValues.leaseTermMax,
      },
      FLEXIBLE: {
        leaseTermMin: defaultValues.leaseTermMin,
        leaseTermMax: defaultValues.leaseTermMax,
      },
    };
    let leaseTerm = FLEXIBLE_LEASE_TERM;
    const currentValues = { leaseTermMin, leaseTermMax };
    Object.keys(this.LEASE_TERM_VALUES).forEach((term) => {
      if (isEqual(currentValues, this.LEASE_TERM_VALUES[term]))
        leaseTerm = term;
    });
    this.setState({ leaseTerm, ...currentValues });
  };

  onChange = (evt) => {
    const { value: leaseTerm } = evt.target;
    this.setState({ leaseTerm, ...this.LEASE_TERM_VALUES[leaseTerm] });
  };

  onClear = () => {
    this.setState(
      {
        ...this.LEASE_TERM_VALUES.FLEXIBLE,
        leaseTerm: "FLEXIBLE",
      },
      this.onDone
    );
  };

  onDone = (toggle = true) => {
    const { update } = this.context;
    const { leaseTerm } = this.state;
    const { onToggle } = this.props;
    update({ ...this.LEASE_TERM_VALUES[leaseTerm], page: 1 });
    if (toggle) onToggle();
  };

  render() {
    const { leaseTerm } = this.state;
    const { open, onToggle } = this.props;

    const leaseTermIsFlexible = determineIfLeaseTermIsFlexible(leaseTerm);

    return (
      <ExternalClickListener onExternalClick={() => open && this.onDone()}>
        <div className={styles.filterWrapper}>
          <Chip
            data-se="lease-filter-button"
            active={open}
            onClick={onToggle}
            className={cn(
              styles.chip,
              !leaseTermIsFlexible && styles.chipSelected
            )}
          >
            {leaseTermIsFlexible ? "Lease Term" : LEASE_TERMS[leaseTerm]}
          </Chip>
          <Menu
            title="Lease Term"
            onClear={this.onClear}
            visible={open}
            onClose={onToggle}
            isDefault={leaseTermIsFlexible}
            className={styles.leaseTermMenu}
            onDone={this.onDone}
            data-qa="lease-term-filter-menu"
          >
            <RadioGroup
              name="leaseTerm"
              value={leaseTerm}
              onChange={this.onChange}
              className={styles.leaseTermRadioGroup}
              labelClassName={styles.labelClassName}
            >
              {Object.keys(LEASE_TERMS).map((key) => (
                <option
                  key={key}
                  value={key}
                  data-qa={`lease-term-${key}`}
                  data-jest={`lease-term-${key}`}
                >
                  {LEASE_TERMS[key]}
                </option>
              ))}
            </RadioGroup>
            <p className={styles.industryAverageExplanation}>
              The industry average for lease terms is usually around 3-5 years
            </p>
          </Menu>
        </div>
      </ExternalClickListener>
    );
  }
}

LeaseTermFilter.contextType = QueryManagerContext;

LeaseTermFilter.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
